import { useEffect, useState } from "react";
import { api, formatApiError, PACKAGE_STYLE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const toNum = (v) => (v === "" || v == null ? null : Number(v));

const PackageEditor = ({ pkg, features, onSaved }) => {
  const [form, setForm] = useState({ ...pkg, max_users: pkg.max_users ?? "", max_portfolios: pkg.max_portfolios ?? "" });
  const toggle = (k) => setForm((f) => ({ ...f, features: f.features.includes(k) ? f.features.filter((x) => x !== k) : [...f.features, k] }));

  const save = async () => {
    try {
      await api.put(`/packages/${pkg.code}`, {
        name: form.name, price: Number(form.price), features: form.features,
        max_users: toNum(form.max_users), max_portfolios: toNum(form.max_portfolios),
        free_drone_per_month: Number(form.free_drone_per_month || 0),
      });
      toast.success(`${form.name} paketi kaydedildi`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Card data-testid={`package-editor-${pkg.code}`} className="p-6 space-y-4">
      <Badge variant="outline" className={PACKAGE_STYLE[pkg.code]}>{pkg.code}</Badge>
      <div><Label>Paket Adı</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
      <div><Label>Aylık Fiyat (TL / 30 gün)</Label><Input data-testid={`package-price-${pkg.code}`} type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Maks. Kullanıcı</Label><Input type="number" placeholder="Sınırsız" value={form.max_users} onChange={(e) => setForm({ ...form, max_users: e.target.value })} /></div>
        <div><Label>Maks. Portföy</Label><Input type="number" placeholder="Sınırsız" value={form.max_portfolios} onChange={(e) => setForm({ ...form, max_portfolios: e.target.value })} /></div>
      </div>
      <div><Label>Aylık Ücretsiz Drone Çekim Hakkı</Label><Input data-testid={`package-free-drone-${pkg.code}`} type="number" min="0" value={form.free_drone_per_month ?? 0} onChange={(e) => setForm({ ...form, free_drone_per_month: e.target.value })} /></div>
      <div className="space-y-2 pt-2 border-t">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Erişilebilir Özellikler</div>
        {Object.entries(features).map(([k, label]) => (
          <label key={k} className="flex items-center justify-between text-sm py-1">
            {label}
            <Switch data-testid={`feature-${pkg.code}-${k}`} checked={form.features.includes(k)} onCheckedChange={() => toggle(k)} />
          </label>
        ))}
      </div>
      <Button data-testid={`save-package-${pkg.code}`} onClick={save} className="w-full bg-slate-900">Kaydet</Button>
    </Card>
  );
};

export default function PackagesPage() {
  const [data, setData] = useState(null);
  const load = async () => setData((await api.get("/packages")).data);
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="packages-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Süper Admin</div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Paketler</h1>
        <p className="text-slate-500 mt-1">Her paketin fiyatını, limitlerini ve erişebileceği özellikleri belirleyin. Değişiklikler tüm ofislere anında yansır.</p>
      </div>

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {data.packages.map((p) => <PackageEditor key={p.code} pkg={p} features={data.features} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
